import { LitElement, html, css, nothing, type PropertyValues } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import './kpi-card';
import './fp3-data-table';
import './fp3-admin';
import './fp3-templates';
import type { DataColumn } from './fp3-data-table';
import { flightMarkStyles, tailDialogStyles, windowsSelectFix } from './dashboard-styles';
import { flightLabel, flightTone } from './flight-status';

interface Kpis {
  target_aircraft: number;
  owners_identified: number;
  operators_identified: number;
  management_companies_identified: number;
  decision_makers_identified: number;
  incumbent_identified: number;
  panasonic_arc: number;
  airshow: number;
  genesys: number;
  flightpath3d: number;
  unknown_incumbent: number;
  flight_observable: number;
  qualified_opportunities: number;
  active_opportunities: number;
  pipeline_value: number;
  won: number;
  needs_research: number;
}

interface ReachContact {
  name: string;
  email?: string | null;
  phone?: string | null;
}

interface AircraftRow {
  id: number;
  tail_number: string | null;
  serial_number: string;
  aircraft_family: string | null;
  model: string | null;
  manufacturer: string | null;
  owner_name: string | null;
  owner_city?: string | null;
  owner_state?: string | null;
  operator_name: string | null;
  incumbent: string;
  needs_research: boolean;
  incumbent_label: string;
  confidence_level: string | null;
  stage_label: string;
  stage: string;
  score: number;
  completeness: number;
  score_parts?: ScorePart[];
  contact_count: number;
  contacts?: ReachContact[];
  flight_status: string;
  flight_data_available?: boolean;
  access_step?: string | null;
}

interface ScorePart { key: string; score: number; weight: number; reason: string; }

interface AircraftDetail extends AircraftRow {
  year_mfr: string | null;
  age: number | null;
  registration_status: string | null;
  street: string | null;
  street2: string | null;
  owner_place: string | null;
  operator_note: string;
  previous_tails: string[];
  flight_status: string;
  flight_data_available: boolean;
  next_action: string | null;
  salesperson: string | null;
  stage: string;
  notes: string | null;
  sales_trigger: string | null;
  score_detail: { score: number; completeness: number; components: ScorePart[] };
  equipment: { system_label: string; confidence_level: string; evidence: string; source_name: string; product: string | null }[];
  contacts: {
    id: number;
    name: string;
    title: string | null;
    role_category: string | null;
    email: string | null;
    phone: string | null;
    source: string;
    confidence: string;
  }[];
}

interface Dashboard {
  kpis: Kpis;
  funnel: { label: string; count: number; pct: number }[];
  incumbents: { id: string; label: string; count: number }[];
  top_accounts: { name: string; summary: string; city: string | null; state: string | null }[];
  call_sheet: AircraftRow[];
  providers: { id: string; name: string; available: boolean; note?: string | null }[];
  target_count_note?: string;
}

function hasReach(contact: ReachContact) {
  return Boolean(contact.email?.trim() || contact.phone?.trim());
}

function prettyPhone(raw: string) {
  const digits = raw.replace(/\D/g, '');
  const local = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (local.length === 10) return `${local.slice(0, 3)}-${local.slice(3, 6)}-${local.slice(6)}`;
  return raw.trim();
}

interface SuggestionBullet {
  before: string;
  count: number;
  after: string;
}

function retrievableSuggestions(rows: AircraftRow[]): { empty: string; bullets: SuggestionBullet[] } {
  if (!rows.length) {
    return { empty: 'No position data available tails have a decision-maker on file yet.', bullets: [] };
  }
  const people = new Map<string, { name: string; via: string; tails: Set<number> }>();
  for (const row of rows) {
    for (const contact of row.contacts || []) {
      if (!hasReach(contact)) continue;
      const phone = contact.phone?.trim() || '';
      const email = contact.email?.trim() || '';
      const via = phone ? prettyPhone(phone) : email;
      if (!via || !contact.name.trim()) continue;
      const key = `${contact.name.trim().toLowerCase()}|${phone.replace(/\D/g, '') || email.toLowerCase()}`;
      const person = people.get(key) ?? { name: contact.name.trim(), via, tails: new Set<number>() };
      person.tails.add(row.id);
      people.set(key, person);
    }
  }
  const ranked = [...people.values()].sort((a, b) => b.tails.size - a.tails.size || a.name.localeCompare(b.name));
  if (!ranked.length) {
    const count = rows.length;
    const noun = count === 1 ? 'tail has' : 'tails have';
    return {
      empty: `${num(count)} position data available ${noun} a decision-maker on file, and none of those contacts have a phone or email yet.`,
      bullets: [],
    };
  }
  return {
    empty: '',
    bullets: ranked.slice(0, 2).map((person, index) => {
      const count = person.tails.size;
      const tailWord = count === 1 ? 'tail' : 'tails';
      return index === 0
        ? { before: `Reaching out to ${person.name} at ${person.via} lets you speak through `, count, after: ` ${tailWord}.` }
        : { before: `${person.name} at ${person.via} covers `, count, after: ` ${tailWord}.` };
    }),
  };
}

function contactPreview(contacts: ReachContact[] | undefined) {
  const list = contacts || [];
  if (!list.length) return null;
  const reachable = list.filter(hasReach);
  const shown = reachable[0] || list[0];
  return { shown, extra: reachable.filter((contact) => contact !== shown).length };
}

@customElement('fp3-brief')
class Fp3Brief extends LitElement {
  @property({ type: Array }) bullets: SuggestionBullet[] = [];
  @property() empty = '';
  @state() private cursor = 0;
  private timer = 0;
  private scriptKey = '';

  static styles = css`
    :host { display: block; margin-top: 1rem; }
    .brief {
      position: relative;
      overflow: hidden;
      padding: 0.85rem 1rem 0.95rem;
      border-radius: 12px;
      border: 1px solid rgba(91, 159, 212, 0.38);
      background:
        linear-gradient(180deg, rgba(91, 159, 212, 0.1), rgba(18, 26, 34, 0.2)),
        #121a22;
    }
    .brief::after {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(100deg, transparent 20%, rgba(183, 215, 238, 0.06) 48%, transparent 78%);
      transform: translateX(-120%);
      animation: brief-sheen 2.4s ease-out 1;
      pointer-events: none;
    }
    .kicker {
      position: relative;
      z-index: 1;
      display: flex;
      align-items: center;
      gap: 0.45rem;
      margin-bottom: 0.4rem;
      font-size: 0.68rem;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: #b7d7ee;
    }
    .dot {
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background: #8fd0a8;
      box-shadow: 0 0 0 0 rgba(143, 208, 168, 0.55);
      animation: brief-pulse 1.6s ease-out infinite;
    }
    p, ul {
      position: relative;
      z-index: 1;
      margin: 0;
      font-size: 1rem;
      line-height: 1.55;
      color: #e8edf2;
    }
    ul { padding: 0; list-style: none; display: grid; gap: 0.35rem; }
    li { margin: 0; }
    .count {
      display: inline-block;
      margin: 0 0.1rem;
      padding: 0 0.32rem;
      border-radius: 6px;
      background: rgba(183, 215, 238, 0.2);
      color: #f4fbff;
      font-weight: 700;
      font-variant-numeric: tabular-nums;
    }
    .caret {
      display: inline-block;
      width: 7px;
      height: 1em;
      margin-left: 2px;
      background: #b7d7ee;
      vertical-align: text-bottom;
      animation: caret-blink 1s steps(1) infinite;
    }
    @keyframes brief-sheen { to { transform: translateX(120%); } }
    @keyframes brief-pulse {
      70% { box-shadow: 0 0 0 8px rgba(143, 208, 168, 0); }
      100% { box-shadow: 0 0 0 0 rgba(143, 208, 168, 0); }
    }
    @keyframes caret-blink { 50% { opacity: 0; } }
    @media (prefers-reduced-motion: reduce) {
      .brief::after, .dot, .caret { animation: none; }
    }
  `;

  disconnectedCallback() {
    window.clearTimeout(this.timer);
    super.disconnectedCallback();
  }

  protected updated() {
    const key = `${this.empty}\n${JSON.stringify(this.bullets)}`;
    if (key === this.scriptKey) return;
    this.scriptKey = key;
    window.clearTimeout(this.timer);
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    if (reduced || !this.script.length) {
      this.cursor = this.script.length;
      return;
    }
    this.cursor = 0;
    this.tick();
  }

  private get script(): { bullet: number; text?: string; count?: number }[] {
    if (!this.bullets.length) {
      return [...this.empty].map((text) => ({ bullet: -1, text }));
    }
    const parts: { bullet: number; text?: string; count?: number }[] = [];
    this.bullets.forEach((bullet, index) => {
      for (const text of bullet.before) parts.push({ bullet: index, text });
      parts.push({ bullet: index, count: bullet.count });
      for (const text of bullet.after) parts.push({ bullet: index, text });
    });
    return parts;
  }

  private tick() {
    if (this.cursor >= this.script.length) return;
    const pace = this.script.length > 180 ? 3 : 1;
    this.cursor = Math.min(this.script.length, this.cursor + pace);
    this.timer = window.setTimeout(() => this.tick(), 16);
  }

  private renderCaret() {
    return html`<span class="caret" aria-hidden="true"></span>`;
  }

  render() {
    const shown = this.script.slice(0, this.cursor);
    const typing = this.cursor < this.script.length;
    const body = this.bullets.length
      ? html`<ul>
          ${this.bullets.map((bullet, index) => {
            const parts = shown.filter((part) => part.bullet === index);
            if (!parts.length) return nothing;
            const count = parts.find((part) => part.count != null)?.count;
            const before = parts.filter((part) => part.count == null).map((part) => part.text).join('');
            const split = count == null ? before.length : bullet.before.length;
            const last = index === this.bullets.length - 1 || !shown.some((part) => part.bullet > index);
            return html`<li>
              ${before.slice(0, split)}${count == null ? nothing : html`<span class="count">${num(count)}</span>`}${before.slice(split)}${typing && last ? this.renderCaret() : nothing}
            </li>`;
          })}
        </ul>`
      : html`<p>${shown.map((part) => part.text).join('')}${typing ? this.renderCaret() : nothing}</p>`;
    return html`
      <div class="brief" role="status" aria-label="Suggestions">
        <div class="kicker"><span class="dot" aria-hidden="true"></span>Suggestions</div>
        ${body}
      </div>
    `;
  }
}

const SALE_STAGES = [
  { id: 'target', label: 'Target' },
  { id: 'contacted', label: 'Contacted' },
  { id: 'pitched', label: 'Pitched' },
  { id: 'sold', label: 'Sold' },
  { id: 'not_interested', label: 'Not interested' },
] as const;

const VALUE_PER_AIRCRAFT = 25000;

const ACCESS_STEPS = [
  { id: 'not_started', n: '0', label: 'Not Started' },
  { id: 'contact_approval', n: '1', label: 'Contact Approval' },
  { id: 'request_submitted', n: '2', label: 'Request Submitted' },
  { id: 'request_status', n: '3', label: 'Request Status' },
] as const;

type AccessStepId = (typeof ACCESS_STEPS)[number]['id'] | 'approved' | 'denied';
type AccessLook = 'done' | 'pending' | 'later' | 'approved' | 'denied';

function accessIndex(step: string | null | undefined) {
  if (step === 'approved' || step === 'denied' || step === 'request_status') return 3;
  const index = ACCESS_STEPS.findIndex((item) => item.id === step);
  return index < 0 ? 0 : index;
}

function accessLook(index: number, step: string | null | undefined): AccessLook {
  if (index === 3 && (step === 'approved' || step === 'denied')) return step;
  const current = accessIndex(step);
  if (index < current) return 'done';
  if (index === current) return 'pending';
  return 'later';
}

@customElement('fp3-access-steps')
class Fp3AccessSteps extends LitElement {
  static styles = css`
    :host { display: block; color: #c5d4e4; }
    ol { list-style: none; margin: 0; padding: 0; display: grid; gap: 0.45rem; }
    li { margin: 0; }
    button.step, .step {
      display: flex; align-items: center; gap: 0.6rem; width: 100%;
      margin: 0; padding: 0.65rem 0.75rem; border-radius: 8px;
      border: 1px solid #314255; background: transparent;
      color: inherit; font: inherit; font-weight: 600; line-height: 1.3; text-align: left;
    }
    button.step { cursor: pointer; }
    .mark {
      display: inline-flex; align-items: center; justify-content: center;
      width: 1.4rem; height: 1.4rem; border-radius: 999px; flex: none;
      font-size: 0.75rem; font-weight: 700; border: 1px solid #4a5d72; color: #8fa3b8;
    }
    .name { min-width: 0; }
    .tag {
      margin-left: auto; flex: none;
      font-size: 0.68rem; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase;
    }
    button.step.later, .step.later { color: #8fa3b8; }
    button.step.pending, .step.pending { color: #d6e6f5; background: rgba(91, 159, 212, 0.16); border-color: #5b9fd4; }
    .pending .mark { background: #5b9fd4; border-color: #5b9fd4; color: #0f1419; }
    .pending .tag { color: #9ec8ea; }
    button.step.done, .step.done { color: #d6e6f5; background: rgba(91, 159, 212, 0.08); border-color: rgba(91, 159, 212, 0.45); }
    .done .mark { background: #5b9fd4; border-color: #5b9fd4; color: #0f1419; }
    button.step.approved, .step.approved { color: #b7e7c8; background: rgba(61, 190, 116, 0.16); border-color: #3dbe74; }
    .approved .mark { background: #3dbe74; border-color: #3dbe74; color: #0f1419; }
    .approved .tag { color: #8fd0a8; }
    button.step.denied, .step.denied { color: #f3c4c4; background: rgba(225, 91, 91, 0.16); border-color: #e15b5b; }
    .denied .mark { background: #e15b5b; border-color: #e15b5b; color: #1a0c0c; }
    .denied .tag { color: #f0a0a0; }
    .outcomes { display: flex; gap: 0.35rem; margin: 0.35rem 0 0 2rem; }
    .outcomes button {
      font: inherit; font-size: 0.75rem; font-weight: 700; padding: 0.28rem 0.55rem;
      border-radius: 6px; cursor: pointer; background: transparent;
    }
    .outcomes .yes { color: #8fd0a8; border: 1px solid #3dbe74; }
    .outcomes .no { color: #f0a0a0; border: 1px solid #e15b5b; }
    .outcomes button[aria-pressed='true'].yes { background: rgba(61, 190, 116, 0.2); }
    .outcomes button[aria-pressed='true'].no { background: rgba(225, 91, 91, 0.2); }
    :host([compact]) ol { gap: 0.18rem; }
    :host([compact]) button.step, :host([compact]) .step {
      padding: 0.08rem 0.28rem; font-size: 0.72rem; background: transparent;
    }
    :host([compact]) .mark { width: 1rem; height: 1rem; font-size: 0.62rem; }
    :host([compact]) .tag { font-size: 0.6rem; }
    :host([compact]) .later { border-color: transparent; }
    :host([compact]) .done { background: transparent; }
  `;

  @property({ type: Boolean, reflect: true }) compact = false;
  @property({ type: Boolean }) interactive = false;
  @property() step: AccessStepId | string = 'not_started';

  private choose(step: AccessStepId | 'approved' | 'denied') {
    this.dispatchEvent(new CustomEvent('access-step', { detail: step }));
  }

  private row(step: (typeof ACCESS_STEPS)[number], index: number) {
    const look = accessLook(index, this.step);
    const done = look === 'done' || look === 'approved' || look === 'denied';
    const tag = look === 'pending'
      ? 'Pending'
      : look === 'approved'
        ? 'Approved'
        : look === 'denied'
          ? 'Denied'
          : '';
    const body = html`
      <span class="mark">${done ? '✓' : step.n}</span>
      <span class="name">${step.label}</span>
      ${tag ? html`<span class="tag">${tag}</span>` : nothing}
    `;
    if (!this.interactive) return html`<div class="step ${look}">${body}</div>`;
    return html`
      <button type="button" class="step ${look}" @click=${() => this.choose(step.id)}>${body}</button>
    `;
  }

  render() {
    const onStatus = accessIndex(this.step) === 3;
    return html`
      <ol>
        ${ACCESS_STEPS.map((step, index) => html`<li>${this.row(step, index)}</li>`)}
      </ol>
      ${this.interactive && onStatus
        ? html`
            <div class="outcomes">
              <button type="button" class="yes" aria-pressed=${this.step === 'approved'} @click=${() => this.choose('approved')}>Approved</button>
              <button type="button" class="no" aria-pressed=${this.step === 'denied'} @click=${() => this.choose('denied')}>Denied</button>
            </div>
          `
        : nothing}
    `;
  }
}

const num = (value: number | undefined) => (value ?? 0).toLocaleString();
const money = (value: number | undefined) =>
  (value ?? 0).toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

@customElement('fp3-sales')
export class Fp3Sales extends LitElement {
  @state() private dash: Dashboard | null = null;
  @state() private loading = true;
  @state() private rows: AircraftRow[] = [];
  @state() private total = 0;
  @state() private selected: AircraftDetail | null = null;
  @state() private error = '';
  @state() private q = '';
  @state() private summaryQ = '';
  @state() private family = '';
  @state() private incumbent = '';
  @state() private needsResearch = false;
  @state() private scoreMin = '';
  @state() private funnel = '';
  @query('dialog.tail-detail') private detailDialog?: HTMLDialogElement;
  @query('dialog.confirm') private confirmDialog?: HTMLDialogElement;
  @state() private confirmTail = '';
  @property({ type: Boolean }) admin = false;
  @state() private pane: 'summary' | 'details' | 'templates' | 'admin' = 'summary';
  @state() private decisionFlight: 'available' | 'blocked' | 'unknown' = 'available';
  @state() private detailTab: 'overview' | 'access' = 'overview';
  @state() private editingContacts = false;
  @state() private draft: { kind: 'email' | 'text'; name: string; to: string; tail: string; family: string } | null = null;
  @state() private previewCopy: { subject: string; body: string } | null = null;
  @query('dialog.mock-message') private draftDialog?: HTMLDialogElement;

  static styles = [css`
    h1 { font-size: clamp(1.35rem, 4vw, 1.75rem); font-weight: 600; margin: 0 0 0.35rem; letter-spacing: -0.02em; }
    .subtitle, .helper { color: #8fa3b8; font-size: 0.85rem; line-height: 1.45; margin: 0 0 1rem; }
    .tabs { display: flex; gap: 0.4rem; margin: 0 0 1rem; }
    .stage-field {
      display: flex; align-items: center; gap: 0.6rem;
      margin: 0 0 0.85rem; font-size: 0.85rem; font-weight: 600;
    }
    .stage-field select { width: auto; min-width: 9rem; }
    .detail-tabs { display: flex; gap: 0.35rem; margin: 0 0 0.75rem; }
    .summary-cards { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 0.75rem; align-items: stretch; }
    .summary-table { margin-top: 1rem; }
    .skel {
      position: relative;
      overflow: hidden;
      border-radius: 10px;
      border: 1px solid #2a3a4a;
      background: #1a2430;
    }
    .skel::after {
      content: '';
      position: absolute;
      inset: 0;
      transform: translateX(-100%);
      background: linear-gradient(90deg, transparent 0%, rgba(232, 237, 242, 0.16) 50%, transparent 100%);
      animation: skel-sweep 1.15s ease-in-out infinite;
    }
    .skel-card { min-height: 108px; }
    .skel-card.short { min-height: 84px; }
    .skel-label { width: 7rem; height: 0.7rem; margin: 0 0 0.45rem; border-radius: 4px; }
    .skel-panel { min-height: 220px; }
    .skel-table { min-height: min(52vh, 480px); margin-top: 1rem; }
    .skel-brief { height: 4.6rem; margin-top: 1rem; }
    .sr {
      position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;
      overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0;
    }
    @keyframes skel-sweep { to { transform: translateX(100%); } }
    @media (prefers-reduced-motion: reduce) {
      .skel::after { animation: none; opacity: 0.35; }
    }
    .decision-card {
      height: 100%; box-sizing: border-box; display: flex; flex-direction: column; gap: 0.55rem;
      padding: 1rem 0.85rem 0.85rem; border-radius: 10px; border: 1px solid #314255;
      border-top: 3px solid #7eb8c9; background: #121a22;
    }
    .decision-label {
      color: #8fa3b8; font-size: 0.72rem; font-weight: 600; letter-spacing: 0.05em; text-transform: uppercase;
    }
    .decision-split { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 0.35rem; margin-top: auto; }
    .decision-split.pair { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    button.seg {
      display: flex; flex-direction: column; align-items: flex-start; gap: 0.15rem;
      width: 100%; min-width: 0; padding: 0.4rem 0.45rem; background: transparent;
      border: 1px solid #2a3a4a; color: inherit; font-weight: 500; text-align: left;
    }
    button.seg .seg-label {
      font-size: 0.62rem; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: #8fa3b8;
    }
    button.seg .seg-value {
      font-size: 1.35rem; font-weight: 700; font-variant-numeric: tabular-nums; line-height: 1.1;
    }
    button.seg.available .seg-value { color: #8fd0a8; }
    button.seg.blocked .seg-value { color: #f0a0a0; }
    button.seg.unknown .seg-value { color: #8fa3b8; }
    button.seg.available[aria-pressed='true'] { background: rgba(61, 190, 116, 0.16); border-color: #3dbe74; color: inherit; }
    button.seg.blocked[aria-pressed='true'] { background: rgba(225, 91, 91, 0.16); border-color: #e15b5b; color: inherit; }
    button.seg.unknown[aria-pressed='true'] { background: rgba(143, 163, 184, 0.14); border-color: #8fa3b8; color: inherit; }
    button.seg.available[aria-pressed='true'] .seg-value { color: #8fd0a8; }
    button.seg.blocked[aria-pressed='true'] .seg-value { color: #f0a0a0; }
    button.seg.unknown[aria-pressed='true'] .seg-value { color: #c5d0de; }
    .kpi-group { margin-bottom: 1rem; }
    .kpi-group h2 { margin: 0 0 0.45rem; font-size: 0.72rem; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; color: #6d8299; }
    .cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 0.75rem; align-items: stretch; }
    .panel { background: #121a22; border: 1px solid #1e2a36; border-radius: 12px; }
    .split { display: grid; grid-template-columns: minmax(220px, 0.8fr) minmax(0, 1.2fr); gap: 0.8rem; margin-bottom: 1rem; align-items: start; }
    .side-stack { display: flex; flex-direction: column; gap: 0.8rem; min-width: 0; }
    .panel { padding: 0.9rem 1rem; }
    .panel h2 { margin: 0 0 0.7rem; font-size: 17px; font-weight: 600; letter-spacing: 0; text-transform: none; color: inherit; }
    .panel .helper { margin: 0.75rem 0 0; }
    .lb-title { font-size: 17px; font-weight: 600; margin: 0 0 6px; }
    button.lb-row {
      display: grid;
      grid-template-columns: 22px 1fr;
      gap: 10px;
      align-items: center;
      width: 100%;
      padding: 9px 8px;
      border: 0;
      border-bottom: 1px solid #243244;
      border-radius: 8px;
      background: transparent;
      color: inherit;
      font: inherit;
      text-align: left;
      cursor: pointer;
    }
    button.lb-row:hover { background: rgba(91, 159, 212, 0.08); }
    button.lb-row[aria-pressed='true'] { background: rgba(91, 159, 212, 0.16); }
    .lb-rank { font-size: 14.5px; font-weight: 600; color: #5b9fd4; }
    .lb-main { display: flex; flex-direction: column; gap: 5px; min-width: 0; }
    .lb-line { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; }
    .lb-name { font-size: 15px; }
    .lb-val { font-size: 15px; font-weight: 600; font-variant-numeric: tabular-nums; }
    .lb-track { height: 6px; }
    .lb-bar { height: 6px; border-radius: 3px; background: #1d6fbf; min-width: 6px; }
    .lb-foot { margin-top: 12px; padding-top: 12px; border-top: 2px solid #314255; }
    .lb-foot-line { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; }
    .lb-foot-label {
      font-size: 13px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; color: #8fa3b8;
    }
    .lb-foot-val { font-size: 20.5px; font-weight: 600; font-variant-numeric: tabular-nums; }
    .lb-cap { font-size: 13px; color: #8fa3b8; margin-top: 4px; }
    .chips { display: flex; flex-wrap: wrap; gap: 0.35rem; }
    button, input, select {
      font: inherit; color: #e8edf2; background: #1a2430; border: 1px solid #2a3a4a; border-radius: 8px;
    }
    button { padding: 0.4rem 0.7rem; cursor: pointer; }
    button[aria-pressed='true'], button.primary { background: #5b9fd4; color: #0f1419; border-color: #5b9fd4; font-weight: 600; }
    input, select { padding: 0.45rem 0.6rem; }
    .toolbar { display: flex; flex-wrap: wrap; gap: 0.45rem; margin-bottom: 0.8rem; }
    .layout { width: 100%; }
    table { width: 100%; border-collapse: collapse; font-size: 0.84rem; }
    th, td { text-align: left; padding: 0.55rem 0.7rem; border-bottom: 1px solid #1e2a36; vertical-align: top; }
    th { color: #8fa3b8; font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.04em; font-weight: 500; }
    tr { cursor: pointer; }
    tr.selected td { background: rgba(91, 159, 212, 0.12); }
    .tail { color: #5b9fd4; font-weight: 600; }
    .muted { color: #8fa3b8; }
    .account { padding: 0.45rem 0; border-bottom: 1px solid #1e2a36; }
    .account strong { display: block; }
    dl { margin: 0; }
    dt { margin-top: 0.75rem; font-size: 0.68rem; letter-spacing: 0.05em; text-transform: uppercase; color: #8fa3b8; }
    dd { margin: 0.15rem 0 0; line-height: 1.4; }
    .banner { margin: 0 0 0.8rem; padding: 0.7rem 0.85rem; border-radius: 10px; background: #1a2430; color: #c5d4e4; line-height: 1.45; }
    .banner.error { background: #2a1618; color: #f0a8a8; }
    .evidence { margin-top: 0.35rem; padding: 0.45rem 0.55rem; background: #0f1419; border-radius: 8px; }
    .contact-head { display: flex; justify-content: space-between; align-items: center; gap: 0.5rem; margin-bottom: 0.35rem; }
    .contact-person { display: flex; justify-content: space-between; align-items: flex-start; gap: 0.6rem; }
    button.remove, button.edit-contacts {
      background: transparent; color: #c5d4e4; border-color: #314255; font-weight: 600;
    }
    button.remove { color: #e8a0a0; border-color: #5a3030; }
    button.message { background: transparent; color: #5b9fd4; border-color: #314255; font-weight: 600; }
    .contact-actions { display: flex; flex-wrap: wrap; gap: 0.35rem; margin-top: 0.35rem; }
    dialog.mock-message {
      width: min(480px, calc(100vw - 2rem));
      max-width: calc(100vw - 2rem);
      margin: auto;
      padding: 1.1rem 1.15rem 1rem;
      border: 1px solid #314255;
      border-radius: 12px;
      background: #121a22;
      color: #e8edf2;
    }
    dialog.mock-message::backdrop { background: rgba(0, 0, 0, 0.55); }
    dialog.mock-message h3 { margin: 0 0 0.35rem; font-size: 1.15rem; }
    .mock-body {
      white-space: pre-wrap;
      margin: 0.7rem 0;
      padding: 0.75rem 0.8rem;
      border-radius: 8px;
      background: #0f1419;
      line-height: 1.45;
    }
    form { display: grid; gap: 0.4rem; margin-top: 0.6rem; }
    @media (max-width: 960px) {
      .split { grid-template-columns: 1fr; }
      .summary-cards { grid-template-columns: 1fr 1fr; }
    }
    @media (max-width: 520px) { .summary-cards { grid-template-columns: 1fr; } }
  `, flightMarkStyles, tailDialogStyles, windowsSelectFix];

  connectedCallback() {
    super.connectedCallback();
    this.reload();
  }

  updated(changed: PropertyValues) {
    if (changed.has('selected')) {
      const dialog = this.detailDialog;
      if (dialog) {
        if (this.selected && !dialog.open) dialog.showModal();
        if (!this.selected && dialog.open) dialog.close();
      }
    }
    if (changed.has('draft')) {
      const dialog = this.draftDialog;
      if (!dialog) return;
      if (this.draft && !dialog.open) dialog.showModal();
      if (!this.draft && dialog.open) dialog.close();
    }
  }

  private openDraft(kind: 'email' | 'text', contact: ReachContact, tail: string, family: string) {
    const to = (kind === 'email' ? contact.email : contact.phone)?.trim() || '';
    if (!to) return;
    this.previewCopy = null;
    this.draft = { kind, name: contact.name, to, tail: tail || 'this aircraft', family };
    void this.loadPreview(kind);
  }

  private async loadPreview(kind: 'email' | 'text') {
    try {
      const res = await fetch(`/api/templates?channel=${kind}`);
      if (!res.ok) return;
      const body = await res.json();
      const list = (body.templates || []) as { subject?: string; body?: string; is_default?: boolean }[];
      const chosen = list.find((item) => item.is_default) || list[0];
      if (!chosen || this.draft?.kind !== kind) return;
      this.previewCopy = { subject: chosen.subject || '', body: chosen.body || '' };
    } catch {
      this.previewCopy = null;
    }
  }

  private fillPreview(text: string) {
    const draft = this.draft;
    if (!draft) return text;
    return text
      .replaceAll('{name}', draft.name)
      .replaceAll('{tail}', draft.tail)
      .replaceAll('{family}', draft.family || '');
  }

  private draftBody() {
    const draft = this.draft;
    if (!draft) return '';
    if (this.previewCopy) {
      const message = this.fillPreview(this.previewCopy.body);
      if (draft.kind === 'email') {
        return `To: ${draft.to}\nSubject: ${this.fillPreview(this.previewCopy.subject)}\n\n${message}`;
      }
      return `To: ${draft.to}\n\n${message}`;
    }
    const about = draft.family ? `${draft.tail} (${draft.family})` : draft.tail;
    if (draft.kind === 'email') {
      return `To: ${draft.to}\nSubject: FlightPath3D for ${draft.tail}\n\nHello ${draft.name},\n\nThis is a preview email about ${about}. Nothing has been sent.`;
    }
    return `To: ${draft.to}\n\nHello ${draft.name}, this is a preview text about ${about}. Nothing has been sent.`;
  }

  private onDialogClose() {
    if (this.selected) this.selected = null;
  }

  private onDialogClick(event: MouseEvent) {
    const dialog = event.currentTarget as HTMLDialogElement;
    if (event.target !== dialog) return;
    const rect = dialog.getBoundingClientRect();
    const inside =
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom;
    if (!inside) dialog.close();
  }

  private async reload() {
    this.error = '';
    try {
      const [dashRes, listRes] = await Promise.all([
        fetch('/api/sales/dashboard'),
        fetch('/api/sales/aircraft?limit=5000'),
      ]);
      if (!dashRes.ok || !listRes.ok) {
        this.error = 'The sales API is not ready yet. Reload after the backend restarts.';
        return;
      }
      this.dash = await dashRes.json();
      const body = await listRes.json();
      this.rows = body.aircraft;
      this.total = body.total;
    } catch {
      this.error = 'The sales API is not reachable.';
    } finally {
      this.loading = false;
    }
  }

  private renderSummarySkeleton() {
    return html`
      <div class="summary-cards" role="status" aria-live="polite">
        <span class="sr">Loading summary</span>
        ${[0, 1, 2, 3].map(() => html`<div class="skel skel-card" aria-hidden="true"></div>`)}
      </div>
      <div class="skel skel-brief" aria-hidden="true"></div>
      <div class="skel skel-table" aria-hidden="true"></div>
    `;
  }

  private renderDetailsSkeleton() {
    return html`
      <div role="status" aria-live="polite"><span class="sr">Loading details</span></div>
      ${[0, 1, 2].map(() => html`
        <section class="kpi-group" aria-hidden="true">
          <div class="skel skel-label"></div>
          <div class="cards">
            ${[0, 1, 2, 3, 4].map(() => html`<div class="skel skel-card short"></div>`)}
          </div>
        </section>
      `)}
      <div class="split" aria-hidden="true">
        <div class="skel skel-panel"></div>
        <div class="side-stack">
          <div class="skel skel-panel"></div>
          <div class="skel skel-panel"></div>
        </div>
      </div>
      <div class="skel skel-table" aria-hidden="true"></div>
    `;
  }

  private flightMark(status: string | undefined) {
    return html`<span class="flight-mark ${flightTone(status)}"><span class="flight-dot" aria-hidden="true"></span>${flightLabel(status)}</span>`;
  }

  private openFlightAware(nNumber: string) {
    window.open(`https://www.flightaware.com/live/flight/${encodeURIComponent(nNumber)}`, '_blank', 'noopener');
    this.confirmTail = nNumber;
    this.updateComplete.then(() => {
      if (!this.confirmDialog?.open) this.confirmDialog?.showModal();
    });
  }

  private async answerVisibility(retrievable: boolean) {
    const nNumber = this.confirmTail;
    const id = this.selected?.id;
    this.confirmDialog?.close();
    if (!nNumber) return;
    const res = await fetch(`/api/tails/${encodeURIComponent(nNumber)}/flight-visibility`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ retrievable }),
    });
    if (!res.ok) {
      this.error = 'Could not save the FlightAware result.';
      return;
    }
    const status = retrievable ? 'available' : 'blocked';
    const previous = this.rows.find((row) => row.id === id || row.tail_number === nNumber);
    this.rows = this.rows.map((row) =>
      row.id === id || row.tail_number === nNumber ? this.withFlight(row, status, retrievable) : row,
    );
    if (this.selected && (this.selected.id === id || this.selected.tail_number === nNumber)) {
      this.selected = this.withFlight(this.selected, status, retrievable);
    }
    this.adjustObservable(previous?.flight_data_available, retrievable);
  }

  private scorePartsForFlight(parts: ScorePart[] | undefined, wasBlocked: boolean, nowBlocked: boolean) {
    if (!parts || wasBlocked === nowBlocked) return parts;
    const rest = parts.filter((part) => part.key !== 'flight');
    if (!nowBlocked) return rest;
    return [...rest, { key: 'flight', score: -25, weight: 25, reason: 'Position data is blocked' }];
  }

  private withFlight<T extends AircraftRow>(row: T, status: string, available: boolean): T {
    const wasBlocked = row.flight_status === 'blocked';
    const nowBlocked = status === 'blocked';
    let score = row.score;
    if (wasBlocked !== nowBlocked) {
      score = nowBlocked ? Math.max(0, score - 25) : Math.min(100, score + 25);
    }
    const known = [
      Boolean(row.owner_name),
      Boolean(row.operator_name),
      row.contact_count > 0,
      Boolean(row.incumbent) && row.incumbent !== 'unknown',
      available,
    ].filter(Boolean).length;
    return {
      ...row,
      flight_status: status,
      flight_data_available: available,
      score,
      completeness: Math.round((100 * known) / 5),
      score_parts: this.scorePartsForFlight(row.score_parts, wasBlocked, nowBlocked),
    };
  }

  private adjustObservable(wasAvailable: boolean | undefined, available: boolean) {
    if (!this.dash || Boolean(wasAvailable) === available) return;
    const delta = available ? 1 : -1;
    this.dash = {
      ...this.dash,
      kpis: {
        ...this.dash.kpis,
        flight_observable: Math.max(0, this.dash.kpis.flight_observable + delta),
      },
    };
  }

  private async openAircraft(id: number) {
    this.editingContacts = false;
    this.detailTab = 'overview';
    const res = await fetch(`/api/sales/aircraft/${id}`);
    if (!res.ok) {
      this.error = 'Could not open that aircraft.';
      return;
    }
    this.selected = await res.json();
  }

  private onSearch(e: Event) {
    const value = (e.target as HTMLInputElement).value;
    const term = value.trim();
    if (term.length >= 3) {
      this.q = value;
      return;
    }
    if (this.q) this.q = '';
  }

  private onSummarySearch(e: Event) {
    const value = (e.target as HTMLInputElement).value;
    const term = value.trim();
    if (term.length >= 3) {
      this.summaryQ = value;
      return;
    }
    if (this.summaryQ) this.summaryQ = '';
  }

  private async setStage(row: { id: number; stage: string }, stage: string) {
    if (!stage || stage === row.stage) return;
    const label = SALE_STAGES.find((item) => item.id === stage)?.label || stage;
    this.rows = this.rows.map((item) => (item.id === row.id ? { ...item, stage, stage_label: label } : item));
    if (this.selected?.id === row.id) this.selected = { ...this.selected, stage, stage_label: label };
    const res = await fetch(`/api/sales/aircraft/${row.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ stage }),
    });
    if (!res.ok) {
      this.error = 'Could not save the stage.';
      this.reload();
      return;
    }
    const updated = await res.json();
    this.rows = this.rows.map((item) => (
      item.id === row.id ? { ...item, stage: updated.stage, stage_label: updated.stage_label } : item
    ));
    if (this.selected?.id === row.id) this.selected = updated;
  }

  private async setAccess(step: string) {
    if (!this.selected || !step || step === (this.selected.access_step || 'not_started')) return;
    const id = this.selected.id;
    this.rows = this.rows.map((item) => (item.id === id ? { ...item, access_step: step } : item));
    this.selected = { ...this.selected, access_step: step };
    const res = await fetch(`/api/sales/aircraft/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ access_step: step }),
    });
    if (!res.ok) {
      this.error = 'Could not save the access step.';
      this.reload();
      return;
    }
    const updated = await res.json();
    this.rows = this.rows.map((item) => (item.id === id ? { ...item, access_step: updated.access_step } : item));
    if (this.selected?.id === id) this.selected = updated;
  }

  private renderStageSelect(row: { id: number; stage: string }) {
    const stop = (event: Event) => event.stopPropagation();
    return html`
      <select
        class="stage-select"
        .value=${row.stage}
        @click=${stop}
        @mousedown=${stop}
        @change=${(event: Event) => {
          stop(event);
          this.setStage(row, (event.target as HTMLSelectElement).value);
        }}
      >
        ${SALE_STAGES.map((stage) => html`<option value=${stage.id} ?selected=${row.stage === stage.id}>${stage.label}</option>`)}
      </select>
    `;
  }

  private async saveStage(e: Event) {
    e.preventDefault();
    if (!this.selected) return;
    const form = e.target as HTMLFormElement;
    const data = new FormData(form);
    const res = await fetch(`/api/sales/aircraft/${this.selected.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        salesperson: data.get('salesperson'),
        next_action: data.get('next_action'),
        notes: data.get('notes'),
        sales_trigger: data.get('sales_trigger'),
      }),
    });
    if (!res.ok) {
      this.error = 'Could not save the opportunity.';
      return;
    }
    this.selected = await res.json();
    this.reload();
  }

  private async addContact(e: Event) {
    e.preventDefault();
    if (!this.selected) return;
    const form = e.target as HTMLFormElement;
    const data = new FormData(form);
    const res = await fetch(`/api/sales/aircraft/${this.selected.id}/contacts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        first_name: data.get('first_name'),
        last_name: data.get('last_name'),
        title: data.get('title') || null,
        source: 'Manual entry',
        confidence: 'medium',
      }),
    });
    if (!res.ok) {
      this.error = 'Enter both a first and last name. Contacts are not inferred.';
      return;
    }
    form.reset();
    this.selected = await res.json();
    this.reload();
  }

  private async removeContact(contactId: number) {
    if (!this.selected) return;
    const res = await fetch(`/api/sales/aircraft/${this.selected.id}/contacts/${contactId}`, { method: 'DELETE' });
    if (!res.ok) {
      this.error = await res.json().then((body) => body.detail).catch(() => 'Could not remove that contact.');
      return;
    }
    this.selected = await res.json();
    this.reload();
  }

  private async addEvidence(e: Event) {
    e.preventDefault();
    if (!this.selected) return;
    const data = new FormData(e.target as HTMLFormElement);
    const res = await fetch(`/api/sales/aircraft/${this.selected.id}/equipment`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        product: data.get('product'),
        evidence: data.get('evidence'),
        source_name: data.get('source_name'),
        confidence_level: data.get('confidence_level'),
        source_type: 'manual',
      }),
    });
    if (!res.ok) {
      this.error = 'Evidence and a source name are required. The system is not guessed.';
      return;
    }
    this.selected = await res.json();
    (e.target as HTMLFormElement).reset();
    this.reload();
  }

  private selectFunnel(label: string) {
    this.funnel = this.funnel === label ? '' : label;
    if (!this.funnel) return;
    this.updateComplete.then(() => {
      this.renderRoot.querySelector('fp3-data-table')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  private matchesFunnel(row: AircraftRow) {
    const stage = row.stage || 'target';
    switch (this.funnel) {
      case 'Target fleet':
        return true;
      case 'Owner identified':
        return Boolean(row.owner_name);
      case 'Operator identified':
        return Boolean(row.operator_name);
      case 'Contact identified':
        return row.contact_count > 0;
      case 'Incumbent identified':
        return row.incumbent !== 'unknown';
      case 'Qualified':
      case 'Contacted':
        return stage === 'contacted' || stage === 'pitched' || stage === 'sold';
      case 'Engaged':
      case 'Proposal':
        return stage === 'pitched' || stage === 'sold';
      case 'Sold':
        return stage === 'sold';
      default:
        return true;
    }
  }

  private flightBucket(status: string | undefined): 'available' | 'blocked' | 'unknown' {
    if (status === 'available') return 'available';
    if (status === 'blocked') return 'blocked';
    return 'unknown';
  }

  private get decisionMakers() {
    return this.rows.filter((row) => row.contact_count > 0);
  }

  private get decisionRows() {
    return this.decisionMakers.filter((row) => this.flightBucket(row.flight_status) === this.decisionFlight);
  }

  private get summaryRows() {
    const query = this.summaryQ.trim().toUpperCase();
    if (query.length < 3) return this.decisionRows;
    return this.decisionRows.filter((row) => {
      const hay = [row.tail_number, row.serial_number, row.owner_name].join(' ').toUpperCase();
      return hay.includes(query);
    });
  }

  private isActiveStage(stage: string | undefined) {
    return stage !== 'target' && stage !== 'not_interested';
  }

  private renderPositionSplit(label: string, aria: string, available: string, blocked: string) {
    const parts = [
      ['available', 'Retrievable', available],
      ['blocked', 'Blocked', blocked],
    ] as const;
    return html`
      <div class="decision-card">
        <div class="decision-label">${label}</div>
        <div class="decision-split pair" role="group" aria-label=${aria}>
          ${parts.map(([id, name, value]) => html`
            <button
              type="button"
              class="seg ${id}"
              aria-pressed=${this.decisionFlight === id}
              @click=${() => { this.decisionFlight = id; }}
            >
              <span class="seg-label">${name}</span>
              <span class="seg-value">${value}</span>
            </button>
          `)}
        </div>
      </div>
    `;
  }

  private renderActivePipeline() {
    const counts = { available: 0, blocked: 0 };
    for (const row of this.rows) {
      if (!this.isActiveStage(row.stage)) continue;
      const bucket = this.flightBucket(row.flight_status);
      if (bucket === 'available' || bucket === 'blocked') counts[bucket] += 1;
    }
    return this.renderPositionSplit(
      'Active pipeline',
      'Active pipeline by position data',
      num(counts.available),
      num(counts.blocked),
    );
  }

  private renderPipelineValue() {
    const counts = { available: 0, blocked: 0 };
    for (const row of this.rows) {
      const bucket = this.flightBucket(row.flight_status);
      if (bucket === 'available' || bucket === 'blocked') counts[bucket] += 1;
    }
    return this.renderPositionSplit(
      'Pipeline value',
      'Pipeline value by position data',
      money(counts.available * VALUE_PER_AIRCRAFT),
      money(counts.blocked * VALUE_PER_AIRCRAFT),
    );
  }

  private renderDecisionMakers() {
    const counts = { available: 0, blocked: 0, unknown: 0 };
    for (const row of this.decisionMakers) counts[this.flightBucket(row.flight_status)] += 1;
    const parts = [
      ['available', 'Retrievable', counts.available],
      ['blocked', 'Blocked', counts.blocked],
      ['unknown', 'Unknown', counts.unknown],
    ] as const;
    return html`
      <div class="decision-card">
        <div class="decision-label">Decision-makers</div>
        <div class="decision-split" role="group" aria-label="Decision-makers by position data">
          ${parts.map(([id, label, count]) => html`
            <button
              type="button"
              class="seg ${id}"
              aria-pressed=${this.decisionFlight === id}
              @click=${() => { this.decisionFlight = id; }}
            >
              <span class="seg-label">${label}</span>
              <span class="seg-value">${num(count)}</span>
            </button>
          `)}
        </div>
      </div>
    `;
  }

  private get shownRows() {
    const query = this.q.trim().toUpperCase();
    const min = this.scoreMin.trim() === '' ? null : Number(this.scoreMin);
    return this.rows.filter((row) => {
      if (this.family && row.aircraft_family !== this.family) return false;
      if (this.incumbent && row.incumbent !== this.incumbent) return false;
      if (this.needsResearch && !row.needs_research) return false;
      if (min !== null && !Number.isNaN(min) && row.score < min) return false;
      if (query) {
        const hay = [
          row.tail_number, row.serial_number, row.owner_name, row.operator_name,
          row.model, row.manufacturer, row.aircraft_family,
        ].join(' ').toUpperCase();
        if (!hay.includes(query)) return false;
      }
      return this.matchesFunnel(row);
    });
  }

  private renderFunnel() {
    const steps = this.dash?.funnel || [];
    const fleet = steps[0]?.count ?? 0;
    const max = Math.max(...steps.map((step) => step.count), 1);
    const owners = steps.find((step) => step.label === 'Owner identified')?.count ?? 0;
    return html`
      <div class="lb-title">Enrichment funnel</div>
      <div>
        ${steps.map((step, index) => html`
          <button
            type="button"
            class="lb-row"
            aria-pressed=${this.funnel === step.label}
            @click=${() => this.selectFunnel(step.label)}
          >
            <div class="lb-rank">${index + 1}</div>
            <div class="lb-main">
              <div class="lb-line">
                <span class="lb-name">${step.label}</span>
                <span class="lb-val">${num(step.count)}</span>
              </div>
              <div class="lb-track">
                <div class="lb-bar" style="width:${Math.max((step.count / max) * 100, 1.5)}%"></div>
              </div>
            </div>
          </button>
        `)}
      </div>
      <div class="lb-foot">
        <div class="lb-foot-line">
          <div>
            <div class="lb-foot-label">Fleet</div>
            <div class="lb-cap">${steps.length} stages · ${num(owners)} owners identified</div>
          </div>
          <div class="lb-foot-val">${num(fleet)}</div>
        </div>
      </div>
    `;
  }

  private renderProfile() {
    const tail = this.selected;
    if (!tail) return nothing;
    const tailNumber = tail.tail_number;
    return html`
      <div class="detail-head">
        <div>
          <p class="detail-kicker">Aircraft</p>
          <h3 class="tail">${tail.tail_number || 'No current tail'}</h3>
          <div class="detail-sub">${tail.aircraft_family || tail.model} · Serial ${tail.serial_number}</div>
        </div>
        <div class="detail-tools">
          ${tailNumber
            ? html`<button type="button" class="external" @click=${() => this.openFlightAware(tailNumber)}>Open in FlightAware</button>`
            : nothing}
          <button type="button" class="dialog-close" @click=${() => this.detailDialog?.close()}>Close</button>
        </div>
      </div>
      ${tail.flight_status === 'blocked'
        ? html`
            <div class="detail-tabs" role="tablist" aria-label="Aircraft">
              <button type="button" role="tab" aria-pressed=${this.detailTab === 'overview'} @click=${() => { this.detailTab = 'overview'; }}>Overview</button>
              <button type="button" role="tab" aria-pressed=${this.detailTab === 'access'} @click=${() => { this.detailTab = 'access'; }}>Access</button>
            </div>
          `
        : nothing}
      ${this.detailTab === 'access' && tail.flight_status === 'blocked'
        ? html`
            <div class="detail-scroll">
              <p class="detail-note">Choose the current step for this blocked tail. Nothing is sent from this page.</p>
              <fp3-access-steps
                interactive
                .step=${tail.access_step || 'not_started'}
                @access-step=${(event: CustomEvent<string>) => this.setAccess(event.detail)}
              ></fp3-access-steps>
            </div>
          `
        : html`<div class="detail-scroll">
        ${tailNumber
          ? html`<p class="detail-note">FlightAware opens in a new tab. After you look, choose whether position data for this tail is retrievable or blocked. The page itself is not copied.</p>`
          : nothing}
        <label class="stage-field">Stage ${this.renderStageSelect(tail)}</label>
        <dl>
          <dt>Registered owner</dt>
          <dd>
            ${tail.owner_name || 'Unknown'}<br />
            ${tail.street || ''}${tail.street2 ? html`<br />${tail.street2}` : nothing}<br />
            ${tail.owner_place || ''}
          </dd>
          <dt>Moving map</dt>
          <dd>
            ${tail.incumbent_label}
            ${tail.confidence_level ? html` · ${tail.confidence_level} confidence` : nothing}
            ${tail.equipment.map((obs) => html`
              <div class="evidence">
                <strong>${obs.system_label}</strong> · ${obs.confidence_level}<br />
                ${obs.evidence}<br />
                <span class="muted">${obs.source_name}</span>
              </div>
            `)}
          </dd>
          <dt>Position data</dt>
          <dd>${this.flightMark(tail.flight_status)}</dd>
          <dt>Contacts</dt>
          <dd>
            <div class="contact-head">
              <span class="muted">${this.editingContacts ? 'Add or remove people you enter. Aircraft-file contacts stay.' : 'Contacts from the aircraft file, plus people added here.'}</span>
              <button type="button" class="edit-contacts" @click=${() => { this.editingContacts = !this.editingContacts; }}>
                ${this.editingContacts ? 'Done' : 'Edit'}
              </button>
            </div>
            ${tail.contacts.length
              ? html`<div class="contact-list">${tail.contacts.map((c) => html`
                  <div class="contact-person">
                    <div>
                      ${c.name}
                      ${c.title ? html`<div class="muted">${c.title}</div>` : nothing}
                      ${c.role_category ? html`<div class="muted">${c.role_category}</div>` : nothing}
                      ${c.email ? html`<div class="muted">${c.email}</div>` : nothing}
                      ${c.phone ? html`<div class="muted">${c.phone}</div>` : nothing}
                      <div class="muted">${c.source === 'Aircraft data' ? 'Aircraft file' : 'Added here'}</div>
                      <div class="contact-actions">
                        ${c.email
                          ? html`<button type="button" class="message" @click=${() => this.openDraft('email', c, tail.tail_number || tail.serial_number, tail.aircraft_family || '')}>Send email</button>`
                          : nothing}
                        ${c.phone
                          ? html`<button type="button" class="message" @click=${() => this.openDraft('text', c, tail.tail_number || tail.serial_number, tail.aircraft_family || '')}>Send text</button>`
                          : nothing}
                      </div>
                    </div>
                    ${this.editingContacts && c.source !== 'Aircraft data'
                      ? html`<button type="button" class="remove" @click=${() => this.removeContact(c.id)}>Remove</button>`
                      : nothing}
                  </div>
                `)}</div>`
              : html`<p class="muted">None on file. Contacts are not inferred from the FAA record.</p>`}
            ${this.editingContacts
              ? html`<form class="contact-form" @submit=${this.addContact}>
                  <div class="name-row">
                    <label>First name<input name="first_name" autocomplete="given-name" required /></label>
                    <label>Last name<input name="last_name" autocomplete="family-name" required /></label>
                  </div>
                  <label>Title<input name="title" placeholder="Optional" /></label>
                  <button class="primary save-contact" type="submit">Save contact</button>
                </form>`
              : nothing}
          </dd>
        </dl>
        ${this.pane === 'details'
          ? html`
              <form @submit=${this.saveStage}>
                <input name="salesperson" placeholder="Salesperson" .value=${tail.salesperson || ''} />
                <input name="next_action" placeholder="Next action" .value=${tail.next_action || ''} />
                <input name="sales_trigger" placeholder="Sales trigger, if known" .value=${tail.sales_trigger || ''} />
                <input name="notes" placeholder="Note" />
                <button class="primary" type="submit">Save opportunity</button>
              </form>
              <form @submit=${this.addEvidence}>
                <input name="product" placeholder="System, e.g. Airshow 4000" required />
                <input name="source_name" placeholder="Source name" required />
                <input name="evidence" placeholder="Evidence tied to this serial or tail" required />
                <select name="confidence_level">
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low" selected>Low</option>
                </select>
                <button type="submit">Record evidence</button>
              </form>
            `
          : nothing}
      </div>`}
    `;
  }

  private get aircraftColumns(): DataColumn<AircraftRow>[] {
    return [
      { key: 'tail', label: 'Tail', emphasis: true, value: (row) => row.tail_number || '' },
      { key: 'serial', label: 'Serial', value: (row) => row.serial_number || '' },
      { key: 'family', label: 'Family', filter: 'select', value: (row) => row.aircraft_family || '' },
      { key: 'owner', label: 'Owner', value: (row) => row.owner_name || '' },
      {
        key: 'location',
        label: 'Location',
        value: (row) => [row.owner_city, row.owner_state].filter(Boolean).join(', '),
      },
      {
        key: 'contact',
        label: 'Contact',
        value: (row) => (row.contacts || []).map((contact) => [contact.name, contact.phone, contact.email].filter(Boolean).join(' ')).join(' '),
        cell: (row) => this.renderContactCell(row),
      },
      {
        key: 'stage',
        label: 'Stage',
        filter: 'select',
        value: (row) => row.stage_label || 'Target',
        cell: (row) => this.renderStageSelect(row),
      },
      {
        key: 'flight',
        label: 'Position',
        filter: 'select',
        value: (row) => flightLabel(row.flight_status),
        mark: (row) => flightTone(row.flight_status),
      },
      ...(this.pane === 'summary' && this.decisionFlight === 'blocked'
        ? [{
            key: 'access',
            label: 'Access',
            value: (row) => {
              const current = ACCESS_STEPS[accessIndex(row.access_step)];
              const outcome = row.access_step === 'approved' ? 'Approved' : row.access_step === 'denied' ? 'Denied' : 'Pending';
              return `${current.n} ${current.label} ${outcome}`;
            },
            cell: (row) => html`<fp3-access-steps compact .step=${row.access_step || 'not_started'}></fp3-access-steps>`,
          } satisfies DataColumn<AircraftRow>]
        : []),
      {
        key: 'score',
        label: 'Score',
        numeric: true,
        value: (row) => String(row.score),
        sortValue: (row) => row.score,
        sub: (row) => `${row.completeness}%`,
        tip: (row) => this.renderScoreTip(row),
      },
    ];
  }

  render() {
    const k = this.dash?.kpis;
    const shown = (value: number | undefined) => (k ? num(value) : '');
    const targetNote = this.dash?.target_count_note
      || 'Serial-number airframes in the G550, Falcon 2000, and Challenger set.';
    const market = [
      ['Target aircraft', shown(k?.target_aircraft), targetNote, '#5b9fd4', targetNote],
      ['Owners identified', shown(k?.owners_identified), 'FAA registered owner is on the current registration.', '#5b9fd4'],
      ['Operators identified', shown(k?.operators_identified), 'An operator was recorded separately from the FAA registrant.', '#7eb8c9'],
      ['Management companies', shown(k?.management_companies_identified), 'A management company was recorded with a source.', '#7eb8c9'],
      ['Decision-makers', shown(k?.decision_makers_identified), 'A contact is on file. Contacts are not inferred.', '#7eb8c9'],
    ];
    const systems = [
      ['Incumbent known', shown(k?.incumbent_identified), 'A moving-map observation with evidence is on file.', '#8fd0a8'],
      ['Panasonic Arc', shown(k?.panasonic_arc), 'Evidence names Panasonic Arc.', '#5b9fd4'],
      ['Airshow', shown(k?.airshow), 'Evidence names a Collins Airshow system.', '#5b9fd4'],
      ['Genesys', shown(k?.genesys), 'Evidence names Genesys.', '#5b9fd4'],
      ['FlightPath3D', shown(k?.flightpath3d), 'Existing customer or a FlightPath3D install on file.', '#8fd0a8'],
      ['Unknown incumbent', shown(k?.unknown_incumbent), 'No defensible moving-map evidence yet.', '#d4a843'],
    ];
    const pipeline = [
      ['Flight-observable', shown(k?.flight_observable), 'Retrievable on the current tail: marked after FlightAware, or returned by AeroAPI.', '#7eb8c9'],
      ['Qualified', shown(k?.qualified_opportunities), 'Stage is Contacted, Pitched, or Sold.', '#5b9fd4'],
      ['Active pipeline', shown(k?.active_opportunities), 'Any stage except Target and Not interested.', '#5b9fd4'],
      ['Pipeline value', k ? money(k.pipeline_value) : '', '$25,000 for each aircraft, in every stage.', '#8fd0a8'],
      ['Sold', shown(k?.won), 'Stage is Sold.', '#8fd0a8'],
      ['Needs research', shown(k?.needs_research), 'Missing an operator, a contact, or incumbent evidence.', '#d4a843'],
    ];
    const decisionLabel = this.decisionFlight === 'available'
      ? 'Retrievable'
      : this.decisionFlight === 'blocked'
        ? 'Blocked'
        : 'Unknown';
    const pane = this.pane === 'admin' && this.admin ? 'admin' : this.pane === 'admin' ? 'summary' : this.pane;
    return html`
      <h1>${pane === 'admin' ? 'Admin' : pane === 'templates' ? 'Templates' : 'Prospects and Potential Revenue'}</h1>
      ${pane === 'admin' || pane === 'templates'
        ? nothing
        : html`<p class="subtitle">
        Target airframes are keyed by manufacturer serial. The FAA name is the registered owner, not the operator.
        Moving-map systems stay Unknown until someone records evidence.
      </p>`}
      <div class="tabs" role="tablist" aria-label="Pipeline">
        <button type="button" role="tab" aria-pressed=${pane === 'summary'} @click=${() => { this.pane = 'summary'; }}>Summary</button>
        <button type="button" role="tab" aria-pressed=${pane === 'details'} @click=${() => { this.pane = 'details'; }}>Details</button>
        <button type="button" role="tab" aria-pressed=${pane === 'templates'} @click=${() => { this.pane = 'templates'; }}>Templates</button>
        ${this.admin
          ? html`<button type="button" role="tab" aria-pressed=${pane === 'admin'} @click=${() => { this.pane = 'admin'; }}>Admin</button>`
          : nothing}
      </div>
      ${this.error ? html`<div class="banner error">${this.error}</div>` : nothing}
      ${pane === 'admin'
        ? html`<fp3-admin></fp3-admin>`
        : pane === 'templates'
          ? html`<fp3-templates></fp3-templates>`
        : this.loading
          ? pane === 'summary'
            ? this.renderSummarySkeleton()
            : this.renderDetailsSkeleton()
        : pane === 'summary'
        ? html`
            <div class="summary-cards">
              <kpi-card label="Target aircraft" value=${shown(k?.target_aircraft)} tooltip=${targetNote} accent="#5b9fd4"></kpi-card>
              ${this.renderDecisionMakers()}
              ${this.renderActivePipeline()}
              ${this.renderPipelineValue()}
            </div>
            ${this.decisionFlight === 'available'
              ? html`<fp3-brief .bullets=${retrievableSuggestions(this.decisionRows).bullets} .empty=${retrievableSuggestions(this.decisionRows).empty}></fp3-brief>`
              : nothing}
            <div class="summary-table">
              <div class="toolbar">
                <input type="search" placeholder="Tail, serial, or owner" @input=${this.onSummarySearch} />
                <span class="muted">${num(this.summaryRows.length)} ${decisionLabel.toLowerCase()} ${this.summaryRows.length === 1 ? 'decision-maker' : 'decision-makers'}. A contact is on file. Contacts are not inferred.</span>
              </div>
              <fp3-data-table
                fill
                .columns=${this.aircraftColumns}
                .rows=${this.summaryRows}
                keyField="id"
                selectedKey=${this.selected?.id ?? ''}
                empty=${this.summaryQ.trim().length >= 3 ? 'No decision-makers match that search.' : 'No decision-makers in this position group.'}
                @select=${(event: CustomEvent<AircraftRow>) => this.openAircraft(event.detail.id)}
              ></fp3-data-table>
            </div>
          `
        : html`
      ${[
        ['Market', market],
        ['Cabin systems', systems],
        ['Pipeline', pipeline],
      ].map(
        ([title, items]) => html`
          <section class="kpi-group">
            <h2>${title}</h2>
            <div class="cards">
              ${(items as string[][]).map(
                ([label, value, tip, accent, sub]) => html`
                  <kpi-card compact label=${label} value=${value} tooltip=${tip} accent=${accent} sub=${sub ?? ''}></kpi-card>
                `,
              )}
            </div>
          </section>
        `,
      )}
      <div class="split">
        <section class="panel">
          ${this.renderFunnel()}
        </section>
        <div class="side-stack">
          <section class="panel">
            <h2>Incumbent systems</h2>
            <div class="chips">
              <button type="button" aria-pressed=${this.incumbent === ''} @click=${() => { this.incumbent = ''; }}>All</button>
              ${(this.dash?.incumbents || []).map((item) => html`
                <button type="button" aria-pressed=${this.incumbent === item.id} @click=${() => { this.incumbent = item.id; }}>
                  ${item.label} ${num(item.count)}
                </button>
              `)}
            </div>
          </section>
          <section class="panel">
            <h2>Accounts</h2>
            ${(this.dash?.top_accounts || []).map((account) => html`
              <div class="account">
                <strong>${account.name}</strong>
                <span class="muted">${account.summary}${account.city ? ` · ${account.city}, ${account.state || ''}` : ''}</span>
              </div>
            `)}
            <p class="helper">A registered-owner count is not treated as a management-company fleet.</p>
          </section>
        </div>
      </div>
      <div class="toolbar">
        <input type="search" placeholder="Tail, serial, or owner" @input=${this.onSearch} />
        <select @change=${(e: Event) => { this.family = (e.target as HTMLSelectElement).value; }}>
          <option value="">All families</option>
          <option>Gulfstream G550</option>
          <option>Falcon 2000</option>
          <option>Bombardier Challenger</option>
        </select>
        <input placeholder="Min score" .value=${this.scoreMin} @input=${(e: Event) => { this.scoreMin = (e.target as HTMLInputElement).value; }} />
        <button type="button" aria-pressed=${this.needsResearch} @click=${() => { this.needsResearch = !this.needsResearch; }}>
          Needs research
        </button>
        <span class="muted">${this.shownRows.length === this.total ? `${num(this.total)} aircraft` : `${num(this.shownRows.length)} of ${num(this.total)}${this.funnel ? ` · ${this.funnel}` : ''}`}</span>
        ${this.funnel ? html`<button type="button" @click=${() => { this.funnel = ''; }}>Clear funnel</button>` : nothing}
      </div>
      <div class="layout">
        <fp3-data-table
          fill
          .columns=${this.aircraftColumns}
          .rows=${this.shownRows}
          keyField="id"
          selectedKey=${this.selected?.id ?? ''}
          empty=${this.shownRows.length === 0 && this.rows.length ? 'No aircraft match.' : 'No aircraft match these column filters.'}
          @select=${(event: CustomEvent<AircraftRow>) => this.openAircraft(event.detail.id)}
        ></fp3-data-table>
      </div>
      <p class="helper">
        Providers:
        ${(this.dash?.providers || []).map((provider) => html`${provider.name} ${provider.available ? 'ready' : 'unavailable'}${provider.note ? ` (${provider.note})` : ''}. `)}
        Pipeline value is $25,000 for each aircraft, in every stage.
      </p>
        `}
      <dialog class="tail-detail" @close=${this.onDialogClose} @click=${this.onDialogClick}>
        ${this.renderProfile()}
      </dialog>
      <dialog class="confirm" @click=${this.onDialogClick}>
        <p class="detail-kicker">FlightAware</p>
        <h3>Is position data for ${this.confirmTail} retrievable?</h3>
        <p class="detail-note">Yes stores this tail as retrievable. No stores it as blocked. This is your answer, not a copy of the FlightAware page.</p>
        <div class="confirm-actions">
          <button type="button" class="yes" @click=${() => this.answerVisibility(true)}>Yes, retrievable</button>
          <button type="button" @click=${() => this.answerVisibility(false)}>No, blocked</button>
        </div>
      </dialog>
      <dialog class="mock-message" @close=${() => { this.draft = null; this.previewCopy = null; }} @click=${this.onDialogClick}>
        ${this.draft
          ? html`
              <p class="detail-kicker">Preview only</p>
              <h3>${this.draft.kind === 'email' ? 'Email' : 'Text'} to ${this.draft.name}</h3>
              <p class="muted">Nothing is sent.</p>
              <div class="mock-body">${this.draftBody()}</div>
              <button type="button" class="dialog-close" @click=${() => { this.draft = null; }}>Close</button>
            `
          : nothing}
      </dialog>
    `;
  }

  private renderScoreTip(row: AircraftRow) {
    const parts = row.score_parts;
    if (!parts?.length) return '';
    return html`
      <div class="tip-head">Score ${row.score} · profile ${row.completeness}%</div>
      ${parts.map((part) => html`<div>${part.score < 0 ? '−' : '+'} ${part.reason} <span class="muted">(${part.score}, weight ${part.weight})</span></div>`)}
    `;
  }

  private renderContactCell(row: AircraftRow) {
    const preview = contactPreview(row.contacts);
    if (!preview) return '—';
    const { shown, extra } = preview;
    const tail = row.tail_number || row.serial_number;
    const family = row.aircraft_family || '';
    return html`
      <div class="reach">
        <div>${shown.name}</div>
        ${shown.phone ? html`<div class="muted">${shown.phone}</div>` : nothing}
        ${shown.email ? html`<div class="muted">${shown.email}</div>` : nothing}
        ${extra > 0 ? html`<div class="muted">+${extra}</div>` : nothing}
        ${shown.email || shown.phone
          ? html`<div class="reach-actions">
              ${shown.email
                ? html`<button type="button" @click=${(event: Event) => { event.stopPropagation(); this.openDraft('email', shown, tail, family); }}>Send email</button>`
                : nothing}
              ${shown.phone
                ? html`<button type="button" @click=${(event: Event) => { event.stopPropagation(); this.openDraft('text', shown, tail, family); }}>Send text</button>`
                : nothing}
            </div>`
          : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'fp3-sales': Fp3Sales;
  }
}
